// Map a drawing's NAME to the platform file UUID.
//
// The old structure app addressed files by its own numeric ids and names;
// step results still carry those (`fisier`, `fisier_id`). The platform
// addresses files by UUID — both the viewer route (`/dossier/dwg/:fileId`)
// and the facade's download/drawing routes want it. The hydrated copies keep
// the original file names, so the name is the stable join key.
//
// One request per project per session: module-level promise cache, same
// pattern as the static catalogs in apiStructura.ts.

import { useEffect, useState } from "react";
import { listFiles, type ProjectFile } from "../api";

const cache = new Map<string, Promise<ProjectFile[]>>();

function filesOf(projectId: string): Promise<ProjectFile[]> {
  let p = cache.get(projectId);
  if (!p) {
    p = listFiles(projectId).catch((e) => {
      cache.delete(projectId); // a failed fetch must not be cached forever
      throw e;
    });
    cache.set(projectId, p);
  }
  return p;
}

/** Returns a lookup: file name -> platform file UUID (null while loading). */
export function useFileIdByName(
  projectId: string
): (name?: string | null) => string | null {
  const [byName, setByName] = useState<Map<string, string> | null>(null);
  useEffect(() => {
    let alive = true;
    filesOf(projectId)
      .then((files) => {
        if (!alive) return;
        setByName(new Map(files.map((f) => [f.name, f.id])));
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [projectId]);
  return (name?: string | null) =>
    name && byName ? byName.get(name) ?? null : null;
}
